import { useCallback, useMemo, useState } from "react";
import { OrderedMap } from "immutable";
import { useQueryParam, StringParam } from "routing/QueryParams";
import usePaginationQueryParameter, { DEFAULT_PAGE } from "hooks/usePaginationQueryParameter";
import useUrlQueryFilters from "components/common/EntityFilters/hooks/useUrlQueryFilters";
export const useWithURLParams = (layoutConfig, defaultFilters) => {
  var _a;
  const [urlQueryFilters, setUrlQueryFilters] = useUrlQueryFilters();
  const [query, setUrlQuery] = useQueryParam("query", StringParam);
  const [slice, setSlice] = useQueryParam("slice", StringParam);
  const urlPagination = usePaginationQueryParameter(void 0, layoutConfig.pageSize, false);
  const [hasUserModifiedFilters, setHasUserModifiedFilters] = useState(false);
  const effectiveFilters = useMemo(() => {
    if (!hasUserModifiedFilters && urlQueryFilters.isEmpty() && defaultFilters && !defaultFilters.isEmpty()) {
      return defaultFilters;
    }
    return urlQueryFilters;
  }, [hasUserModifiedFilters, urlQueryFilters, defaultFilters]);
  const searchParams = useMemo(
    () => {
      var _a2;
      return {
        query,
        slice,
        sliceCol: (_a2 = layoutConfig.slicing) == null ? void 0 : _a2.sliceColumn,
        page: urlPagination.page,
        pageSize: layoutConfig.pageSize,
        sort: layoutConfig.sort,
        filters: effectiveFilters
      };
    },
    [
      query,
      slice,
      (_a = layoutConfig.slicing) == null ? void 0 : _a.sliceColumn,
      urlPagination.page,
      layoutConfig.pageSize,
      layoutConfig.sort,
      effectiveFilters
    ]
  );
  const onChangeFilters = useCallback(
    (newUrlQueryFilters) => {
      setHasUserModifiedFilters(true);
      urlPagination.resetPage();
      setUrlQueryFilters(newUrlQueryFilters);
    },
    [urlPagination, setUrlQueryFilters]
  );
  const onChangeSlicingFilter = useCallback(
    (newSlice) => {
      urlPagination.resetPage();
      setSlice(newSlice);
    },
    [setSlice, urlPagination]
  );
  const resetFilters = useCallback(() => {
    setHasUserModifiedFilters(false);
    setUrlQueryFilters(OrderedMap({}));
    setSlice(void 0);
    urlPagination.resetPage();
  }, [setSlice, setUrlQueryFilters, urlPagination]);
  return useMemo(
    () => ({
      searchParams,
      setQuery: setUrlQuery,
      onChangeFilters,
      onChangeSlicingFilter,
      paginationState: urlPagination,
      resetFilters
    }),
    [searchParams, onChangeFilters, onChangeSlicingFilter, resetFilters, setUrlQuery, urlPagination]
  );
};
export const useWithLocalState = (layoutConfig, defaultFilters) => {
  var _a;
  const defaultState = useMemo(
    () => ({
      query: "",
      page: DEFAULT_PAGE,
      filters: defaultFilters != null ? defaultFilters : OrderedMap(),
      slice: void 0
    }),
    [defaultFilters]
  );
  const [transientFetchOptions, setTransientFetchOptions] = useState(defaultState);
  const setPagination = useCallback(({ page, pageSize }) => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      page,
      pageSize
    }));
  }, []);
  const resetPage = useCallback(() => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      page: DEFAULT_PAGE,
      sort: layoutConfig.sort
    }));
  }, [layoutConfig.sort]);
  const onChangeFilters = useCallback((newFilters) => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      page: DEFAULT_PAGE,
      filters: newFilters
    }));
  }, []);
  const onChangeSlicingFilter = useCallback((slice) => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      page: DEFAULT_PAGE,
      slice
    }));
  }, []);
  const setQuery = useCallback((newQuery) => {
    setTransientFetchOptions((cur) => ({
      ...cur,
      query: newQuery,
      page: DEFAULT_PAGE
    }));
  }, []);
  const searchParams = useMemo(
    () => {
      var _a2;
      return {
        ...transientFetchOptions,
        sliceCol: (_a2 = layoutConfig.slicing) == null ? void 0 : _a2.sliceColumn,
        pageSize: layoutConfig.pageSize,
        sort: layoutConfig.sort
      };
    },
    [transientFetchOptions, layoutConfig.pageSize, (_a = layoutConfig.slicing) == null ? void 0 : _a.sliceColumn, layoutConfig.sort]
  );
  const resetFilters = useCallback(() => {
    setTransientFetchOptions(defaultState);
  }, [defaultState]);
  return useMemo(
    () => ({
      searchParams,
      setQuery,
      onChangeFilters,
      onChangeSlicingFilter,
      paginationState: {
        page: searchParams.page,
        pageSize: searchParams.pageSize,
        resetPage,
        setPagination
      },
      resetFilters
    }),
    [searchParams, onChangeFilters, onChangeSlicingFilter, resetFilters, resetPage, setPagination, setQuery]
  );
};
